<script setup lang="ts" generic="T">
import { computed, type Component, type PropType } from 'vue';
import Field from './Field.vue';
import { useFieldProps, fieldPropDefs } from './props';
import RadioButton from 'primevue/radiobutton';

type Option = {
    value: T;
    label: Component | string;
    disabled: boolean;
    id: string;
    class: string | string[] | Record<string, boolean> | undefined;
    style: string | Record<string, string> | undefined;
    radioClass: string | string[] | Record<string, boolean> | undefined;
    radioStyle: string | Record<string, string> | undefined;
    labelClass: string | string[] | Record<string, boolean> | undefined;
    labelStyle: string | Record<string, string> | undefined;
    name: string;
};

type OptionInput = Partial<Option> & { value: T } & (T extends string
        ? { name?: string }
        : { name: string });

const props = defineProps({
    ...fieldPropDefs,
    options: {
        type: Array as PropType<OptionInput[]>,
        required: true,
    },
});

const emit = defineEmits(['update:modelValue', 'click:option']);
const { id: fieldId, fieldProps, modelValue } = useFieldProps(props, emit);

function normalizeOption(input: OptionInput): Option {
    const name = input.name ?? (input.value as string);

    return {
        name,
        label: input.label ?? name,
        disabled: !!input.disabled,
        value: input.value,
        id: input.id ?? `${fieldId}--${name}`,
        class: input.class,
        style: input.style,
        radioClass: input.radioClass,
        radioStyle: input.radioStyle,
        labelClass: input.labelClass,
        labelStyle: input.labelStyle,
    };
}

const normalizedOptions = computed<Option[]>(() => {
    return props.options.map(normalizeOption);
});

function optionClickHandler(event: MouseEvent, option: Option) {
    emit('click:option', event, option);
    if (!option.disabled) {
        modelValue.value = option.value;
    }
}
</script>

<template>
    <Field class="radiogroup-field" v-bind="fieldProps">
        <div
            v-for="option of normalizedOptions"
            :key="`option-${option.id}`"
            class="radiogroup-field-option"
            :class="[
                option.class,
                {
                    'option-disabled': option.disabled,
                },
            ]"
            :style="option.style"
            @click="optionClickHandler($event, option)"
        >
            <RadioButton
                v-model="modelValue"
                :value="option.value"
                :input-id="option.id"
                :disabled="option.disabled"
                :name="option.name"
                :class="option.radioClass"
                :style="option.radioStyle"
            />
            <label
                :for="option.id"
                class="option-label"
                :class="option.labelClass"
                :style="option.labelStyle"
                ><slot name="option-label" :option="option" :value="modelValue">
                    <template v-if="typeof option.label === 'string'">{{
                        option.label
                    }}</template>
                    <template v-else
                        ><component :is="option.label"
                    /></template> </slot
            ></label>
        </div>
    </Field>
</template>

<style lang="scss">
.radiogroup-field {
    .radiogroup-field-option {
        display: flex;
        padding: 0.25rem;
        align-items: center;

        .option-label {
            margin-left: 0.5rem;
        }

        &.option-disabled {
            .option-label {
                color: var(--text-color-secondary);
            }
        }
    }
}
</style>
